/**
 * <variant-matrix>
 * カラー×サイズ マトリックスの行ごとカート追加／再入荷通知を処理する。
 * - カート追加は標準 /cart/add.js。Section Rendering API で cart-notification / cart-drawer を更新。
 * - お気に入りは Wishlist King（<wishlist-button-collection>）が自前で処理するため、ここでは扱わない。
 * 参考：Hirabayashi-Production/seilin-ec の c_variant-matrix.js を Duffer 用に整理。
 */
if (!customElements.get('variant-matrix')) {
  customElements.define(
    'variant-matrix',
    class VariantMatrix extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;

        this.panel = this.querySelector('[data-vm-panel]');
        this.done = this.querySelector('[data-vm-done]');
        this.trigger = this.querySelector('[data-vm-open]');
        this.spMedia = window.matchMedia('(max-width: 740px)');

        this.initEventListeners();
        this.initModal();
        // 表示時にカート状態を反映（既に在庫上限まで入ってる行は再入荷通知に）
        this.refreshAvailability();
      }

      // イベント委譲：ボタンのクラスで挙動が決まるので、切替はクラス変更だけで済む
      initEventListeners() {
        this.addEventListener('click', (event) => {
          const addBtn = event.target.closest('.c-variant-matrix__button--add');
          if (addBtn) {
            this.handleAddToCart({ currentTarget: addBtn });
            return;
          }
          const soldBtn = event.target.closest('.c-variant-matrix__button--soldout');
          if (soldBtn) {
            this.handleBackInStock({ currentTarget: soldBtn });
          }
        });
      }

      // カート内数量が在庫上限に達した行のボタンを「再入荷通知」に切り替える
      async refreshAvailability() {
        const cartRoot = window.Shopify?.routes?.root || '/';
        let cart;
        try {
          cart = await fetch(`${cartRoot}cart.js`).then((r) => r.json());
        } catch (e) {
          return;
        }
        const qtyById = {};
        (cart.items || []).forEach((item) => {
          qtyById[item.variant_id] = (qtyById[item.variant_id] || 0) + item.quantity;
        });

        this.querySelectorAll('.c-variant-matrix__row').forEach((row) => {
          const limit = row.dataset.limit;
          // data-limit が無い＝在庫追跡なし/在庫切れ購入可 → 上限なし
          if (limit === undefined || limit === '') return;
          const variantId = parseInt(row.dataset.variantId, 10);
          const inCart = qtyById[variantId] || 0;
          if (inCart >= parseInt(limit, 10)) {
            this.convertRowToSoldOut(row);
          }
        });
      }

      // 行のカート追加ボタンを「再入荷通知」ボタンに差し替える（クラス変更のみ・委譲なので再バインド不要）
      convertRowToSoldOut(row) {
        const button = row.querySelector('.c-variant-matrix__button--add');
        if (!button) return;
        button.classList.remove('c-variant-matrix__button--add', 'loading');
        button.classList.add('c-variant-matrix__button--soldout');
        button.removeAttribute('aria-busy');
        // 売切ボタンの文言：force_modal（コーデ）は「在庫なし」、通常（商品詳細）は「再入荷通知」
        button.textContent = this.dataset.soldoutButtonText || this.dataset.backInStockText || '再入荷通知';
        // force_modal はリンク無し（押せない）に統一
        if (this.dataset.soldoutDisabled === 'true') button.disabled = true;
        row.classList.add('c-variant-matrix__row--sold_out');
        const stockLabel = row.querySelector('.c-variant-matrix__stock-label');
        if (stockLabel) stockLabel.textContent = this.dataset.soldOutText || '在庫なし';
      }

      /* --- SP モーダル制御 --- */
      initModal() {
        this.querySelectorAll('[data-vm-open]').forEach((el) =>
          el.addEventListener('click', () => this.openModal())
        );
        this.querySelectorAll('[data-vm-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeModal())
        );
        this.querySelectorAll('[data-vm-close-done]').forEach((el) =>
          el.addEventListener('click', () => this.closeDone())
        );

        // 完了モーダルの背景（オーバーレイ）クリックで閉じる
        if (this.done) {
          this.done.addEventListener('click', (e) => {
            if (e.target === this.done) this.closeDone();
          });
        }

        // モーダルのオーバーレイ（::before 領域）クリックで閉じる。
        // ::before はホスト要素の一部なので target がホスト自身のときだけ反応（パネル内クリックは無視）。
        // PC中央ダイアログ用。SPはパネルが全画面のためオーバーレイに当たらず発火しない。
        this.addEventListener('click', (e) => {
          if (e.target === this && this.classList.contains('is-open')) this.closeModal();
        });

        const select = this.querySelector('[data-vm-color-select]');
        if (select) {
          select.addEventListener('change', (e) => this.filterColor(e.target.value));
          this.applyInitialColor(select);
        } else {
          this.syncTriggerLabel(this.querySelector('.c-variant-matrix__group'));
        }
      }

      openModal() {
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      closeModal() {
        this.classList.remove('is-open');
        document.body.style.overflow = '';
      }

      // カラーセレクトで選んだグループだけ表示（SPモーダル用）
      filterColor(color) {
        let activeGroup = null;
        this.querySelectorAll('.c-variant-matrix__group').forEach((group) => {
          const isActive = group.dataset.color === color;
          group.classList.toggle('is-active', isActive);
          if (isActive) activeGroup = group;
        });
        this.syncModalImage(activeGroup);
        this.syncTriggerLabel(activeGroup);
      }

      syncModalImage(group) {
        const image = this.querySelector('.c-variant-matrix__modal-img');
        if (!image || !group || !group.dataset.imageSrc) return;
        image.src = group.dataset.imageSrc;
        if (group.dataset.imageSrcset) image.srcset = group.dataset.imageSrcset;
        image.alt = group.dataset.imageAlt || image.alt;
      }

      // 選択カラー内に購入可能なサイズが無い場合、SPの入口も「再入荷通知」にする
      syncTriggerLabel(group) {
        if (!this.trigger || !group) return;
        const hasAddButton = Boolean(group.querySelector('.c-variant-matrix__button--add'));
        const hasSoldOutButton = Boolean(group.querySelector('.c-variant-matrix__button--soldout'));
        this.trigger.textContent = !hasAddButton && hasSoldOutButton
          ? (this.dataset.backInStockText || '再入荷通知')
          : (this.trigger.dataset.addToCartText || 'カートに入れる');
      }

      // URLで指定されたバリアントのカラーを初期選択にする（指定が無ければ先頭カラー）。
      applyInitialColor(select) {
        const variantId = new URLSearchParams(window.location.search).get('variant');
        if (variantId) {
          const variantButton = this.querySelector(`[data-variant-id="${CSS.escape(variantId)}"]`);
          const group = variantButton?.closest('.c-variant-matrix__group');
          const color = group?.dataset.color;
          if (color) {
            select.value = color;
          }
        }
        // 該当optionが無ければ select.value は空になるため、先頭カラーへ戻す。
        if (!select.value) {
          select.value = select.options[0]?.value || '';
        }
        this.filterColor(select.value);
      }

      openDone() {
        if (this.done) this.done.hidden = false;
      }

      closeDone() {
        if (this.done) this.done.hidden = true;
        this.closeModal();
      }

      async handleAddToCart(event) {
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;

        if (!variantId || button.classList.contains('loading')) {
          return;
        }

        const spinner = button.querySelector('.c-variant-matrix__spinner');

        try {
          button.classList.add('loading');
          button.setAttribute('aria-busy', 'true');
          if (spinner) spinner.classList.remove('hidden');

          const payload = {
            id: variantId,
            quantity: 1,
          };

          // 予約商品の発送予定日を line item property として送る
          const shippingTime = button.dataset.propertyShippingTime;
          if (shippingTime) {
            payload.properties = { '発送予定日': shippingTime };
          }

          const body = JSON.stringify(payload);

          const cartUrl = window.Shopify?.routes?.root
            ? `${window.Shopify.routes.root}cart/add.js`
            : '/cart/add.js';

          const response = await fetch(cartUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: body,
          });

          if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = this.dataset.errorMessage || 'カートに追加できませんでした';
            throw new Error(errorData.description || errorMessage);
          }

          await response.json();

          // カート最新状態を取得して Focal の cart:updated を発火
          // → ヘッダーの <cart-count>（detail.cart.item_count を読む）や cart-notification が更新される
          const cartRoot = window.Shopify?.routes?.root || '/';
          const cart = await fetch(`${cartRoot}cart.js`).then((r) => r.json());
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } })
          );

          // 在庫上限に達した行を再入荷通知に切り替え
          this.refreshAvailability();

          // PC・SPとも「カートに追加しました」完了モーダルを表示（Figma 69-11581 / 69-2131）
          this.openDone();
        } catch (error) {
          console.error('[c-variant-matrix] add to cart error:', error);
          const tryAgainMessage =
            this.dataset.tryAgainMessage || 'カートに追加できませんでした。もう一度お試しください。';
          alert(error.message || tryAgainMessage);
        } finally {
          button.classList.remove('loading');
          button.setAttribute('aria-busy', 'false');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      handleBackInStock(event) {
        const button = event.currentTarget;
        const variantId = parseInt(button.dataset.variantId, 10);

        // Back in Stock: Restock Alerts（AMP / useamp）でそのバリアントの再入荷通知フォームを開く
        // API: window.BIS.popup.ready → BIS.popup.form.show({ variantId })
        // ※モーダルの見た目（オーバーレイ色等）はアプリ管理画面のCSSで調整
        if (window.BIS && window.BIS.popup) {
          const openForm = () => window.BIS.popup.form.show({ variantId: variantId });
          if (window.BIS.popup.ready && typeof window.BIS.popup.ready.then === 'function') {
            window.BIS.popup.ready.then(openForm);
          } else {
            openForm();
          }
          return;
        }

        console.warn('[c-variant-matrix] Back in Stock widget not ready. variant:', variantId);
      }
    }
  );
}
